import {
  BoldEnum,
  GuestAddAddress as GuestAddress,
  Heading,
  HeadingEnum,
  Text,
  TextEnum,
} from '@desquared/wave-components-library';
import { useRouter } from 'next/router';
import { useEffect, useState, useMemo } from 'react';
import { GuestAddressStore } from '@/store/GuestAddress';
import { Shop } from '@/store/shop';
import { PostalCode } from '@/store/isAddressRegistered';
import GuestUserStore from '@/store/guestUser';
import { useToast } from '@/contexts/ToastContext';
import useDeliveryServices from '@/hooks/useDeliveryServices';
import { getShopsForDeliveryV3 } from '@/api/Shops';
import { SideSheetStore } from '@/store/sideSheet';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import { useConfiguration } from '@/contexts/ConfigContext';
import { SelectedShopId } from '@/store/isAddressRegistered';
import getLayoutProps from '@/utils/layout/layout';
import styles from './GuestAddAddress.module.css';

export function GuestAddAddress({ responsiveLogo }) {
  const router = useRouter();
  const [addressUnsupported, setAddressUnsuported] = useState(false);
  const { showToast } = useToast();
  const { selectedService, getCollectionTypes } = useDeliveryServices();
  const preselectedAddress = GuestUserStore.getValue()?.address;
  const t = useTranslations();
  const { config } = useConfiguration();

  const addressRestrictions = useMemo(() => {
    if (!config) return;

    let restrictions = {};

    if (config?.fields?.address) {
      for (const key of ['street', 'streetNo', 'postalCode']) {
        restrictions[key] = {
          maxLength: config?.fields?.address?.[key]?.maxLength,
          minLength: config?.fields?.address?.[key]?.minLength,
          required: true,
          errorMessage: {
            required: t('common.guest_address.errors.required'),
            maxLength: t('common.guest_address.erros.maxLength', { number: config?.fields?.address?.[key]?.maxLength }),
            minLength: t('common.guest_address.errors.minLength', {
              number: config?.fields?.address?.[key]?.minLength,
            }),
          },
        };
      }

      if (config?.fields?.address?.postalCode?.format === 'ALPHANUMERIC') {
        restrictions.postalCode.pattern = /^[A-Za-z0-9]+$/;
        restrictions.postalCode.errorMessage = {
          ...restrictions.postalCode.errorMessage,
          pattern: t('common.guest_address.errors.alphanumeric'),
        };
      } else {
        restrictions.postalCode.pattern = /^[0-9]+$/;
        restrictions.postalCode.errorMessage = {
          ...restrictions.postalCode.errorMessage,
          pattern: t('common.guest_address.errors.numeric'),
        };
      }
    }

    if ('postalCode' in restrictions) {
      restrictions['postal'] = { ...restrictions.postalCode };
      delete restrictions['postalCode'];
    }

    return restrictions;
  }, [config, t]);

  const unsuportedImageQuery = useQuery({
    queryKey: ['empty-states', router.locale],
    queryFn: () => getLayoutProps({ headerProps: { selectedIndex: -1 } }, router.locale),
    select: data => {
      return data?.emptyStates?.address_empty_state;
    },
  });

  function handleAddressUpdate(value, valid) {
    GuestAddressStore.update({
      isValid: valid,
      submitAddress: async () => {
        const shops = await getShopsForDeliveryV3(selectedService?.id, value.postal);

        if (!shops || shops?.length === 0) {
          setAddressUnsuported(true);
          return;
        } else {
          showToast(t('common.guest_address.toast.supported', { service: selectedService.title }));
          SideSheetStore.close();
        }

        Shop.setShopByDeliveryId(selectedService?.id, shops);
        Shop.setSelectedShop(shops[0]);
        SelectedShopId.update(shops[0]._id);
        PostalCode.update(value.postal);
        GuestUserStore.update({
          address: {
            postalCode: value.postal,
            street: value.street,
            streetNo: value.streetNo,
          },
        });
      },
    });
  }

  useEffect(() => {
    GuestAddressStore.update({
      ...GuestAddressStore.getValue(),
      ...(addressUnsupported && {
        addressUnsupportedConfiguration: {
          onSubmitText: t('common.guest_address.unsuported.new_address_btn'),
          onSubmit: () => {
            setAddressUnsuported(false);
          },
          actionText: t('common.guest_address.unsuported.close'),
        },
      }),
    });
  }, [addressUnsupported]);

  return (
    <div className={styles.container}>
      {addressUnsupported ? (
        <div className={styles.addressUnsupportedContainer}>
          <img
            src={unsuportedImageQuery?.data ? unsuportedImageQuery?.data.url : null}
            alt={t('common.guest_address.unsuported.title')}
          ></img>
          <div className={styles.addressUnsupportedText}>
            <Heading bold={BoldEnum.Bold} type={HeadingEnum.HeadingL}>
              {t('common.guest_address.unsuported.title')}
            </Heading>
            <Text type={TextEnum.Body}>{t('common.guest_address.unsuported.description')}</Text>
          </div>
        </div>
      ) : (
        <>
          <GuestAddress
            restrictions={addressRestrictions}
            autocompleteConfig={{ restrictAutoCompleteToCountry: process.env.defaultAutocompleteCountryCode }}
            onValidAddressChange={handleAddressUpdate}
            defaultValues={
              preselectedAddress
                ? {
                    postal: preselectedAddress.postalCode,
                    street: preselectedAddress.street,
                    streetNo: preselectedAddress.streetNo,
                  }
                : undefined
            }
            map={{
              apiKey: process.env.NEXT_PUBLIC_GOOGLE_API_KEY,
              center: {
                lat: process.env.defaultMapCenter.lat,
                lon: process.env.defaultMapCenter.lng,
              },
              locale: router.locale,
              stigmas: [],
              logo: responsiveLogo,
            }}
            wordings={{
              footerBtn: t('common.guest_address.header'),
              formPlaceholders: {
                address: t('common.guest_address.form_fields.address'),
                street: t('common.guest_address.form_fields.street'),
                streetNo: t('common.guest_address.form_fields.street_no'),
                postal: t('common.guest_address.form_fields.postal'),
              },
            }}
          />
        </>
      )}
    </div>
  );
}
